
import * as angular from "angular";
import * as moment from "moment";
import { IPolyline } from "@typings/google.maps.angular";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { CoordsModel } from "../../../../main/components/livemap/model/CoordsModel";
import { GoogleMarkerService, MarkerAnchorPosition } from "@services/googleMarker.service";
import { MapService } from "@services/map.service";
import { PlotService } from "@services/plot.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import { IVideoPlaybackEvent } from "../../../../main/components/videos/models/IVideoPlaybackEvent";
import { VideoPlaybackState } from "../../../../main/components/videos/enums/VideoPlaybackState";
import { TranslationService } from "@services/translation.service";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";

interface IVeriMapVideoEventBindings {

    veriMap: IVeriMapComponent;

    videoPlaybackEvent: IVideoPlaybackEvent;
}

class VeriMapVideoEventComponentController implements angular.IComponentController, IVeriMapVideoEventBindings {
    veriMap: IVeriMapComponent;

    videoPlaybackEvent: IVideoPlaybackEvent;

    videoPolyline: IPolyline;
    videoEvents: IPlotEvent[] = [];
    videoPlot: IPlot;

    static $inject = ["plotService", "veriMarkerService", "googleMarkerService", "mapService", "translationService"];

    constructor(
        private readonly plotService: PlotService,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly mapService: MapService,
        private readonly translationService: TranslationService    ) {
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (onChangesObj["videoPlaybackEvent"]) {
            if (this.videoPlaybackEvent.state === VideoPlaybackState.Started) {
                if (!this.videoPlaybackEvent.video.VideoExtensionData) {
                    this.videoPlaybackEvent.video.VideoExtensionData = {};
                }

                if (!this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData) {
                    this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData = [this.videoPlaybackEvent.currentGpsData];
                }

                this.createVideoMapEvents();
                this.updateVideoPolyline();
            }

            this.updateGpsPlot();
        }
    }

    $onInit(): void {
    }

    // ---------------

    private updateVideoPolyline() {
        var latLngs: google.maps.LatLng[] = [];

        this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData.forEach(gps => {
            latLngs.push(new google.maps.LatLng(gps.Latitude, gps.Longitude));
        });

        this.videoPolyline = this.mapService.createPolyline(1, latLngs, null, null);
        this.veriMap.fitMap(latLngs);
    }

    private createVideoMapEvents() {
        this.videoEvents = [];

        let startGps = this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData[0];
        let endGps = this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData[this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData.length - 1];

        if (!startGps) {
            startGps = this.videoPlaybackEvent.currentGpsData;
        }

        if (!endGps) {
            endGps = this.videoPlaybackEvent.currentGpsData;
        }

        if (startGps && this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData.length <= 1) {
            this.videoEvents.push(this.googleMarkerService.createEventMarker(
                {
                    Utc: moment(this.videoPlaybackEvent.video.VideoStartTime).toISOString(),
                },
                {
                    label: this.translationService.t("veriMapVideoEvent.videoLocation"),
                    subIcon: "flag",
                    showLabel: true,
                    position: { lat: startGps.Latitude, lng: startGps.Longitude }
                }));

            this.veriMap.fitMap([new google.maps.LatLng(startGps.Latitude, startGps.Longitude)]);
            return;
        }

        if (!startGps || !endGps) {
            console.log("No start or end gps for", this.videoPlaybackEvent, startGps, endGps);
            return;
        }

        const leftToRight = startGps.Longitude < endGps.Longitude;
        const startAnchorPosition = leftToRight ? MarkerAnchorPosition.Left : MarkerAnchorPosition.Right;
        const endAnchorPosition = leftToRight ? MarkerAnchorPosition.Right : MarkerAnchorPosition.Left;

        const fakeStartPlot = {
            Utc: moment(this.videoPlaybackEvent.video.VideoStartTime).toISOString(),
            Latitude: startGps.Latitude,
            Longitude: startGps.Longitude
        };

        const fakeEndPlot = {
            Utc: moment(this.videoPlaybackEvent.video.VideoEndTime).toISOString(),
            Latitude: endGps.Latitude,
            Longitude: endGps.Longitude
        };

        var startEvent = this.googleMarkerService.createEventMarker(fakeStartPlot, { label: this.translationService.t("veriMapVideoEvent.videoStart"), subIcon: "play_arrow", showLabel: true, anchorPosition: startAnchorPosition });
        const endEvent = this.googleMarkerService.createEventMarker(fakeEndPlot, { label: this.translationService.t("veriMapVideoEvent.videoEnd"), subIcon: "flag", showLabel: true, anchorPosition: endAnchorPosition });

        this.videoEvents.push(startEvent);
        this.videoEvents.push(endEvent);
    }

    private updateGpsPlot() {
        if (!this.videoPlaybackEvent || this.videoPlaybackEvent.video.VideoExtensionData.VideoGpsData.length <= 1) {
            return;
        }

        this.videoPlot = {
            Id: Math.random(),
            Latitude: this.videoPlaybackEvent.currentGpsData.Latitude,
            Longitude: this.videoPlaybackEvent.currentGpsData.Longitude,
            Utc: this.videoPlaybackEvent.currentGpsData.Timestamp,
            Velocity: this.videoPlaybackEvent.currentGpsData.Speed,
            Direction: this.videoPlaybackEvent.currentGpsData.Heading
        };

        this.plotService.preparePlotForMap(this.videoPlot);

        // Should prepare plot for map do this?
        this.videoPlot.veriMarkerOptions = this.veriMarkerService.createVeriMarkerOptions(this.videoPlot, true);
        this.googleMarkerService.generateMarkerOptions(this.videoPlot, this.videoPlot.veriMarkerOptions).then(options => {
            this.videoPlot.markerModel = {
                id: this.videoPlot.Id,
                options: options
            };
        });
    }
}

export class VeriMapVideoEventComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;
    require: { [index: string]: string; };

    constructor() {
        this.bindings = {
            videoPlaybackEvent: "<"
        };

        this.require = {
            veriMap: "^veriMap"
        };

        this.controller = VeriMapVideoEventComponentController;
        this.template = require("./veriMapVideoEvent.html");
    }
}
